import PropTypes from "prop-types";
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";

// --- fetchFeeAmount moved from feeAmountApi.js ---
/**
 * Fetch fee amount for given parameters from the backend.
 * @param {Object} params
 * @param {number} college_id
 * @param {string} fee_head_name
 * @param {number} no_of_subjects
 * @returns {Promise<number>} fee_amount
 */
export async function fetchFeeAmount({ college_id, fee_head_name, no_of_subjects }) {
  try {
    const res = await axios.get("/api/enrollsupply/fee-amount", {
      params: { college_id, fee_head_name, no_of_subjects },
    });
    if (res.data && res.data.success) {
      return res.data.fee_amount;
    }
    return 0;
  } catch (err) {
    console.error("Error fetching fee amount:", err);
    return 0;
  }
}

const EnrollSupply = () => {
  const [feeHeads, setFeeHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [student, setStudent] = useState(undefined);
  const [studentLoading, setStudentLoading] = useState(true);
  const [subjectsDialogOpen, setSubjectsDialogOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [currentFeeHead, setCurrentFeeHead] = useState(null);
  // Map: fee_head_id -> array of selected subjects (objects with course_code and course_name)
  const [selectedSubjectsMap, setSelectedSubjectsMap] = useState({});
  // Map: fee_head_id -> fee_amount
  const [feeAmountMap, setFeeAmountMap] = useState({});

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user") || "null");
    if (!user?.client_id || !user?.user_email) return;
    setStudentLoading(true);
    const fetchStudent = async () => {
      try {
        const url = `/api/students/by-email/${user.client_id}/${encodeURIComponent(
          user.user_email
        )}`;
        const res = await axios.get(url);
        const data = res.data?.data || res.data || null;
        setStudent(data && data.student_id ? data : null);
      } catch (err) {
        console.error("Error fetching student:", err);
        setStudent(null);
      } finally {
        setStudentLoading(false);
      }
    };
    fetchStudent();
  }, []);

  useEffect(() => {
    if (!student || student === null) return;
    const fetchFeeHeads = async () => {
      try {
        setLoading(true);
        setError(null);
        const user = JSON.parse(sessionStorage.getItem("user") || "null");
        const clientId = user?.client_id || 1;
        const res = await axios.get(`/api/enrollsupply/active-fee-heads/${clientId}`);
        if (res.data && res.data.success) {
          setFeeHeads(res.data.data);
        } else {
          setError("Failed to fetch fee heads");
          setSnackbarMessage("Failed to fetch fee heads");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching fee heads");
        setSnackbarMessage(err.response?.data?.message || "Error fetching fee heads");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchFeeHeads();
  }, [student]);

  // Track applied fee_head_ids from FeeBookExamList
  const [appliedFeeHeadIds, setAppliedFeeHeadIds] = useState([]);

  // Callback to update appliedFeeHeadIds from FeeBookExamList (memoized)
  const handleAppliedFeeHeadIdsUpdate = useCallback((ids) => {
    setAppliedFeeHeadIds(ids);
  }, []);

  const columns = [
    { field: "fee_head_id", headerName: "ID", width: 100 },
    {
      field: "fee_head_name",
      headerName: "Fee Head Name",
      width: 400,
      renderCell: (params) => (
        <span style={{ whiteSpace: "normal", wordBreak: "break-all" }} title={params.value}>
          {params.value}
        </span>
      ),
    },
    {
      field: "select_subjects",
      headerName: "Select Subjects",
      width: 180,
      sortable: false,
      renderCell: (params) =>
        appliedFeeHeadIds.includes(params.row.fee_head_id) ? (
          <Button variant="outlined" size="small" disabled sx={{ color: "#888" }}>
            Already applied
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleOpenSubjectsDialog(params.row)}
            sx={{ color: "#fff" }}
          >
            Select Subjects
          </Button>
        ),
    },
    {
      field: "fee_amount",
      headerName: "Fee Amount",
      width: 130,
      valueGetter: (params) => {
        const feeHeadId = params.row.fee_head_id;
        const selected = selectedSubjectsMap[feeHeadId] || [];
        if (!selected.length) return "";
        return feeAmountMap[feeHeadId] || "";
      },
    },
    {
      field: "apply",
      headerName: "Apply",
      width: 120,
      sortable: false,
      renderCell: (params) =>
        appliedFeeHeadIds.includes(params.row.fee_head_id) ? (
          <Button variant="outlined" size="small" disabled sx={{ color: "#888" }}>
            Already applied
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => handleApply(params.row)}
          >
            Apply
          </Button>
        ),
    },
  ];

  // For dialog: use local state for selected subjects, but update map on close
  const [dialogSelectedSubjects, setDialogSelectedSubjects] = useState([]);
  const handleOpenSubjectsDialog = async (feeHead) => {
    console.log("handleOpenSubjectsDialog feeHead:", feeHead);
    setCurrentFeeHead(feeHead);
    setSubjectsDialogOpen(true);
    setSubjects([]);
    setDialogSelectedSubjects(selectedSubjectsMap[feeHead.fee_head_id] || []);
    setSubjectsLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "null");
      const clientId = user?.client_id || 1;
      const student_rollno = student?.student_rollno || "";
      const res = await axios.get(`/api/enrollsupply/subjects`, {
        params: {
          clientId,
          fee_head_name: feeHead.fee_head_name,
          student_rollno,
        },
      });
      if (res.data && res.data.success) {
        setSubjects(res.data.data);
      } else {
        setSnackbarMessage("Failed to fetch subjects");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Error fetching subjects");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const handleCloseSubjectsDialog = async (save = false) => {
    setSubjectsDialogOpen(false);
    setSubjects([]);
    if (save && currentFeeHead) {
      setSelectedSubjectsMap((prev) => ({
        ...prev,
        [currentFeeHead.fee_head_id]: dialogSelectedSubjects,
      }));
      // Fetch fee amount for this selection
      if (student && dialogSelectedSubjects.length > 0) {
        const fee_amount = await fetchFeeAmount({
          college_id: student.college_id,
          fee_head_name: currentFeeHead.fee_head_name,
          no_of_subjects: dialogSelectedSubjects.length,
        });
        setFeeAmountMap((prev) => ({ ...prev, [currentFeeHead.fee_head_id]: fee_amount }));
      }
    }
    setDialogSelectedSubjects([]);
    setCurrentFeeHead(null);
  };

  const handleSubjectToggle = (subject) => {
    setDialogSelectedSubjects((prev) =>
      prev.some((s) => s.course_code === subject.course_code)
        ? prev.filter((s) => s.course_code !== subject.course_code)
        : [...prev, subject]
    );
  };

  // --- Add this function to fetch both fee heads and fee book entries ---
  const refreshAllData = async () => {
    // Fetch fee heads
    const user = JSON.parse(sessionStorage.getItem("user") || "null");
    const clientId = user?.client_id || 1;
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`/api/enrollsupply/active-fee-heads/${clientId}`);
      if (res.data && res.data.success) {
        setFeeHeads(res.data.data);
      } else {
        setError("Failed to fetch fee heads");
        setSnackbarMessage("Failed to fetch fee heads");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching fee heads");
      setSnackbarMessage(err.response?.data?.message || "Error fetching fee heads");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle Apply button
  const handleApply = async (feeHead) => {
    const selected = selectedSubjectsMap[feeHead.fee_head_id] || [];
    if (!selected.length) {
      setSnackbarMessage("Please select at least one subject.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    try {
      let year_number = feeHead.year_number || null;
      let semester_number = feeHead.semester_number || null;
      // Fetch fee amount for this selection (ensure up-to-date)
      let fee_amount = feeAmountMap[feeHead.fee_head_id];
      if (!fee_amount) {
        fee_amount = await fetchFeeAmount({
          college_id: student.college_id,
          fee_head_name: feeHead.fee_head_name,
          no_of_subjects: selected.length,
        });
        setFeeAmountMap((prev) => ({ ...prev, [feeHead.fee_head_id]: fee_amount }));
      }

      // Create comma-separated list of course codes for remarks
      const remarks = selected.map((s) => s.course_code).join(",");

      const payload = {
        fee_head_id: feeHead.fee_head_id,
        student_id: student.student_id,
        student_rollno: student.student_rollno,
        college_id: student.college_id,
        semester_number,
        year_number,
        fee_amount,
        remarks: remarks,
        workflow_status: "DRAFT",
        created_by: student.student_rollno,
      };
      await axios.post("/api/enrollsupply/apply-fee-book", payload);
      setSnackbarMessage("Fee book entry applied successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (typeof refreshAllData === "function") refreshAllData();
    } catch (err) {
      let msg = err.response?.data?.message || "Error applying fee book entry.";
      if (msg.includes("already applied")) {
        msg = "Fee book entry already applied.";
      }
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      if (typeof refreshAllData === "function") refreshAllData();
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h6" component="div">
            Active Fee Heads for Supply
          </Typography>
        </Toolbar>
        {loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : feeHeads.length === 0 ? (
          <Alert severity="info">No active fee heads available for today.</Alert>
        ) : (
          <Box sx={{ height: 400, width: "100%" }}>
            {console.log("[DEBUG] DataGrid feeHeads:", feeHeads)}
            <DataGrid
              rows={feeHeads.map((fh) => {
                // Ensure fee_head_name is always present
                let fee_head_name = fh.fee_head_name;
                if (!fee_head_name && fh.fee_head && fh.fee_head.fee_head_name) {
                  fee_head_name = fh.fee_head.fee_head_name;
                }
                if (!fee_head_name && fh.name) {
                  fee_head_name = fh.name;
                }
                if (!fee_head_name) {
                  fee_head_name = "UNKNOWN";
                }
                return {
                  ...fh,
                  id: fh.fee_head_id,
                  fee_head_name,
                };
              })}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableSelectionOnClick
            />
          </Box>
        )}

        {/* Dialog should be outside the conditional rendering but inside the Paper */}
        <Dialog
          open={subjectsDialogOpen}
          onClose={() => handleCloseSubjectsDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Select Subjects for Fee Head</DialogTitle>
          <DialogContent>
            {subjectsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 100,
                }}
              >
                <CircularProgress />
              </Box>
            ) : subjects.length === 0 ? (
              <Alert severity="info">No subjects found for this fee head.</Alert>
            ) : (
              <List>
                {subjects.map((subject) => (
                  <ListItem
                    key={subject.course_code}
                    onClick={() => handleSubjectToggle(subject)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={dialogSelectedSubjects.some(
                          (s) => s.course_code === subject.course_code
                        )}
                        tabIndex={-1}
                        disableRipple
                        sx={{
                          color: "#000000",
                          "&.Mui-checked": {
                            color: "#000000",
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: 24,
                            border: "2px solid #000000",
                            borderRadius: "4px",
                            backgroundColor: dialogSelectedSubjects.some(
                              (s) => s.course_code === subject.course_code
                            )
                              ? "#000000"
                              : "transparent",
                          },
                          "& .MuiSvgIcon-root path": {
                            fill: dialogSelectedSubjects.some(
                              (s) => s.course_code === subject.course_code
                            )
                              ? "#ffffff"
                              : "transparent",
                          },
                        }}
                        inputProps={{
                          "aria-labelledby": `checkbox-list-label-${subject.course_code}`,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={`checkbox-list-label-${subject.course_code}`}
                      primary={`${subject.course_code} - ${subject.course_name}`}
                      primaryTypographyProps={{ fontWeight: 500, color: "#1a1515" }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleCloseSubjectsDialog(false)}>CLOSE</Button>
            <Button
              onClick={() => handleCloseSubjectsDialog(true)}
              variant="contained"
              color="primary"
              style={{ color: "#fff" }}
            >
              SAVE
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>

      {/* --- Fee Book List for Exam Fees --- */}
      <Paper elevation={2} sx={{ p: 2, mt: 4 }}>
        <Toolbar>
          <Typography variant="h6" component="div">
            Fee Book Entries (Exam Fees)
          </Typography>
        </Toolbar>
        <FeeBookExamList
          student={student}
          onAppliedFeeHeadIdsUpdate={handleAppliedFeeHeadIdsUpdate}
          refreshAllData={refreshAllData}
        />
      </Paper>
    </Box>
  );
};

FeeBookExamList.propTypes = {
  student: PropTypes.shape({
    student_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    student_rollno: PropTypes.string,
    college_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onAppliedFeeHeadIdsUpdate: PropTypes.func,
  refreshAllData: PropTypes.func,
};

// --- FeeBookExamList Component ---
function FeeBookExamList({ student, onAppliedFeeHeadIdsUpdate, refreshAllData }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  useEffect(() => {
    if (!student?.student_id) return;
    const fetchFeeBook = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all fee_book entries for this student
        const res = await axios.get(`/api/enrollsupply/fee-books/by-student/${student.student_id}`);
        // Filter for fee_head_name ending with 'EXAMFEE'
        let data = res.data?.items || res.data?.data || [];
        data = data.filter((row) => {
          // Support both row.fee_head_name and row.fee_head?.fee_head_name
          const name = row.fee_head_name || row.fee_head?.fee_head_name || "";
          return name.trim().toUpperCase().endsWith("EXAMFEE");
        });
        const mappedRows = data.map((r) => ({
          ...r,
          fee_head_name: r.fee_head_name || r.fee_head?.fee_head_name || "",
          id: r.fee_book_id,
        }));
        setRows(mappedRows);
        // Update applied fee_head_ids in parent
        if (onAppliedFeeHeadIdsUpdate) {
          const ids = mappedRows.map((r) => r.fee_head_id);
          onAppliedFeeHeadIdsUpdate(ids);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch fee book data");
      } finally {
        setLoading(false);
      }
    };
    fetchFeeBook();
  }, [student, onAppliedFeeHeadIdsUpdate]);

  const [menuAnchorEls, setMenuAnchorEls] = useState({});

  const handleMenuOpen = (event, id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };
  const handleMenuClose = (id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: null }));
  };

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const handleEdit = async (row) => {
    // Check if a receipt exists for this fee_head_name and student_rollno
    try {
      const res = await axios.get("/api/enrollsupply/fee-receipt/exists", {
        params: {
          fee_head_name: row.fee_head_name,
          student_rollno: row.student_rollno,
        },
      });
      if (res.data && res.data.exists) {
        setSnackbarMessage("Cannot edit: Receipt already exists for this fee head and student.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        handleMenuClose(row.fee_book_id);
        return;
      }
    } catch (err) {
      setSnackbarMessage("Error checking receipt existence.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      handleMenuClose(row.fee_book_id);
      return;
    }
    setEditForm({ ...row });
    setEditDialogOpen(true);
    handleMenuClose(row.fee_book_id);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditForm(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  // For edit: handle select subjects and auto-fill amount
  const [editSubjectsDialogOpen, setEditSubjectsDialogOpen] = useState(false);
  const [editSubjects, setEditSubjects] = useState([]);
  const [editSubjectsLoading, setEditSubjectsLoading] = useState(false);
  const [editSelectedSubjects, setEditSelectedSubjects] = useState([]); // array of subject objects

  const handleEditSubjects = async (editRow) => {
    setEditSubjectsDialogOpen(true);
    setEditSubjects([]);
    setEditSubjectsLoading(true);

    // Parse existing remarks to get selected subject codes
    const existingCodes = editRow.remarks ? editRow.remarks.split(",") : [];
    setEditSelectedSubjects(existingCodes.map((code) => ({ course_code: code })));

    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "null");
      const clientId = user?.client_id || 1;
      const student_rollno = student?.student_rollno || "";
      let fee_head_name = editRow.fee_head_name;

      const res = await axios.get(`/api/enrollsupply/subjects`, {
        params: {
          clientId,
          fee_head_name,
          student_rollno,
        },
      });

      if (res.data && res.data.success) {
        setEditSubjects(res.data.data);
      } else {
        setSnackbarMessage("Failed to fetch subjects");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Error fetching subjects");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setEditSubjectsLoading(false);
    }
  };

  const handleEditSubjectToggle = (subject) => {
    setEditSelectedSubjects((prev) =>
      prev.some((s) => s.course_code === subject.course_code)
        ? prev.filter((s) => s.course_code !== subject.course_code)
        : [
            ...prev,
            {
              course_code: subject.course_code,
              course_name: subject.course_name,
            },
          ]
    );
  };

  const handleEditSubjectsDialogClose = async (save = false) => {
    setEditSubjectsDialogOpen(false);
    if (save && editForm && student) {
      // Find the correct fee_head from feeHeads (top grid)
      const feeHead = feeHeads.find((fh) => fh.fee_head_id === editForm.fee_head_id);
      const perSubjectFee = feeHead ? feeHead.fee_amount : 0;
      setEditForm((prev) => ({
        ...prev,
        remarks: editSelectedSubjects.map((s) => s.course_code).join(","),
        fee_amount: editSelectedSubjects.length * perSubjectFee,
      }));
    }
    setEditSelectedSubjects([]);
  };

  const handleEditFormSubmit = async () => {
    if (!editForm) return;
    try {
      const res = await axios.put(`/api/enrollsupply/fee-books/${editForm.fee_book_id}`, editForm);
      console.log("Edit response:", res.data);
      if (res.data && res.data.success) {
        setSnackbarMessage("Fee book entry edited successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEditDialogOpen(false);
        setEditForm(null);
        if (typeof refreshAllData === "function") refreshAllData();
      } else {
        let msg = res.data?.message || "Error editing fee book entry.";
        setSnackbarMessage(msg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Edit error:", err);
      let msg = err.response?.data?.message || "Error editing fee book entry.";
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async (row) => {
    // Check if a receipt exists for this fee_head_name and student_rollno
    try {
      const res = await axios.get("/api/enrollsupply/fee-receipt/exists", {
        params: {
          fee_head_name: row.fee_head_name,
          student_rollno: row.student_rollno,
        },
      });
      if (res.data && res.data.exists) {
        setSnackbarMessage("Cannot delete: Receipt already exists for this fee head and student.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        handleMenuClose(row.fee_book_id);
        return;
      }
    } catch (err) {
      setSnackbarMessage("Error checking receipt existence.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      handleMenuClose(row.fee_book_id);
      return;
    }

    // Perform delete if allowed
    try {
      const response = await axios.delete(`/api/enrollsupply/fee-books/${row.fee_book_id}`);
      if (response.data && response.data.success) {
        setSnackbarMessage("Fee book entry deleted successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        if (typeof refreshAllData === "function") refreshAllData();
      } else {
        setSnackbarMessage(response.data?.message || "Error deleting fee book entry.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Error deleting fee book entry.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    handleMenuClose(row.fee_book_id);
  };

  const columns = [
    { field: "fee_book_id", headerName: "ID", flex: 0.5, minWidth: 50 },
    { field: "fee_head_id", headerName: "Fee Head ID", flex: 1, minWidth: 120 },
    { field: "fee_head_name", headerName: "Fee Head Name", flex: 1.5, minWidth: 180 },
    { field: "fee_amount", headerName: "Amount", flex: 1, minWidth: 100 },
    {
      field: "remarks",
      headerName: "Remarks",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => {
        const remarks = params.value;

        // If remarks is null or undefined
        if (!remarks) return "";

        // If remarks is a string (comma-separated list of course codes)
        if (typeof remarks === "string") {
          const codes = remarks.split(",").filter((code) => code.trim());
          return codes.join(", ");
        }

        // If remarks is an array of objects
        if (Array.isArray(remarks)) {
          return remarks
            .map((item) => {
              if (item && typeof item === "object") {
                if (item.course_code && item.course_name) {
                  return `${item.course_code} - ${item.course_name}`;
                }
                return item.course_code || item.course_name || JSON.stringify(item);
              }
              return String(item);
            })
            .join(", ");
        }

        return String(remarks);
      },
    },
    { field: "workflow_status", headerName: "Status", flex: 1, minWidth: 100 },
    {
      field: "created_on",
      headerName: "Created On",
      flex: 1,
      minWidth: 120,
      valueGetter: (p) => p.row.created_on?.slice(0, 10),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row.fee_book_id)}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEls[params.row.fee_book_id]}
            open={Boolean(menuAnchorEls[params.row.fee_book_id])}
            onClose={() => handleMenuClose(params.row.fee_book_id)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => handleEdit(params.row)}>Edit</MenuItem>
            <MenuItem onClick={() => handleDelete(params.row)}>Delete</MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      {loading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
          />
          {/* Edit Fee Book Dialog */}
          <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Fee Book Entry</DialogTitle>
            <DialogContent>
              {editForm && (
                <Box
                  component="form"
                  sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <TextField
                      label="Fee Head Name"
                      value={editForm.fee_head_name}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={() => handleEditSubjects(editForm)}
                      sx={{ color: "#fff", height: 40 }}
                    >
                      Select Subjects
                    </Button>
                    <TextField
                      label="Amount"
                      value={editForm.fee_amount}
                      InputProps={{ readOnly: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <TextField
                    label="Remarks"
                    value={editForm.remarks || ""}
                    onChange={(e) => handleEditFormChange("remarks", e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditDialogClose}>CANCEL</Button>
              <Button
                variant="contained"
                onClick={handleEditFormSubmit}
                color="primary"
                sx={{ color: "#fff" }}
              >
                UPDATE
              </Button>
            </DialogActions>
          </Dialog>
          {/* Edit Subjects Dialog */}
          <Dialog
            open={editSubjectsDialogOpen}
            onClose={() => handleEditSubjectsDialogClose(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Select Subjects for Fee Head</DialogTitle>
            <DialogContent>
              {editSubjectsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 100,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : editSubjects.length === 0 ? (
                <Alert severity="info">No subjects found for this fee head.</Alert>
              ) : (
                <List>
                  {editSubjects.map((subject) => {
                    const code = subject.course_code;
                    const name = subject.course_name || "";
                    const displayText = name ? `${code} - ${name}` : code;
                    return (
                      <ListItem
                        key={code}
                        onClick={() => handleEditSubjectToggle(subject)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={editSelectedSubjects.some((s) => s.course_code === code)}
                            tabIndex={-1}
                            disableRipple
                            sx={{
                              color: "#000000",
                              "&.Mui-checked": {
                                color: "#000000",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 24,
                                border: "2px solid #000000",
                                borderRadius: "4px",
                                backgroundColor: editSelectedSubjects.some(
                                  (s) => s.course_code === code
                                )
                                  ? "#000000"
                                  : "transparent",
                              },
                              "& .MuiSvgIcon-root path": {
                                fill: editSelectedSubjects.some((s) => s.course_code === code)
                                  ? "#ffffff"
                                  : "transparent",
                              },
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={displayText} />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleEditSubjectsDialogClose(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => handleEditSubjectsDialogClose(true)}
                color="primary"
                sx={{ color: "#fff" }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EnrollSupply;
